import { useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, apiWrite, list } from '../lib/api'
import { useAuth } from '../auth'
import { Empty, PageHead, Skel, Stat } from '../components/ui'
import { thaiDateTime } from '../lib/format'

/**
 * Multi-cloud security and policy enforcement audit (part 101).
 *
 * The page runs a static scan of configuration files that exist in this repository —
 * apps/edge/wrangler.toml, docker-compose.ci.yml and infra/k8s/*.yaml — and shows one verdict per
 * control with the path and line that produced it. It never calls Cloudflare, Docker or a
 * Kubernetes API, so "ผ่าน" on this screen means "the checked file contains the required
 * setting", not "the running platform enforces it". An Ingress annotation the controller ignores
 * and a NetworkPolicy on a CNI that does not implement NetworkPolicy both pass the file check.
 *
 * Controls that need a live system (the RLS control) are shown as "ต้องตรวจด้วยมือ" and are never
 * counted as a pass. The scan itself is stored: every press of "สแกนรีโป" creates one immutable
 * scan with its per-control evidence, and the history selector opens an earlier run.
 *
 * The page is not routed from App.tsx in this part; the API mount is also intentionally unmounted.
 * Both are one-line wiring changes reviewed with the first operator who needs the console.
 */

type Status = 'pass' | 'fail' | 'advisory'
type Severity = 'low' | 'medium' | 'high' | 'critical'
type Category = 'cloudflare' | 'docker' | 'kubernetes' | 'database'

interface Control {
  id: string
  code: string
  title: string
  description: string
  sort_order: number
  category: Category
  severity: Severity
  reference: string
  enforceable: boolean
  enabled: boolean
  rationale: string
  created_at: string
  updated_at: string
}

interface Evidence {
  path: string
  line: number
  snippet: string
  note?: string
}

interface Result {
  id: string
  scan_id: string
  control_id: string
  control_code: string
  status: Status
  severity: Severity
  summary: string
  evidence: Evidence[]
  created_at: string
}

interface Scan {
  id: string
  repo_name: string
  files_scanned: number
  controls_total: number
  passed: number
  failed: number
  advisory: number
  duration_ms: number
  scanned_at: string
  triggered_by?: string
  request_id?: string
}

interface ScanResponse {
  scan: Scan
  results: Result[]
}

interface ResultsResponse {
  scan: Scan | null
  results: Result[]
}

const STATUS_LABEL: Record<Status, string> = {
  pass: 'ผ่าน',
  fail: 'ไม่ผ่าน',
  advisory: 'ต้องตรวจด้วยมือ',
}

const STATUS_TONE: Record<Status, string> = {
  pass: 'var(--color-emerald-soft)',
  fail: 'var(--color-crimson)',
  advisory: 'var(--color-amber)',
}

const SEVERITY_LABEL: Record<Severity, string> = {
  critical: 'วิกฤต',
  high: 'สูง',
  medium: 'ปานกลาง',
  low: 'ต่ำ',
}

const CATEGORY_LABEL: Record<Category, string> = {
  cloudflare: 'Cloudflare Worker',
  docker: 'Docker Compose',
  kubernetes: 'Kubernetes',
  database: 'ฐานข้อมูล',
}

const CATEGORY_ORDER: Category[] = ['cloudflare', 'docker', 'kubernetes', 'database']

function errorText(err: unknown, fallback: string): string {
  return err instanceof Error ? err.message : fallback
}

function duration(ms: number): string {
  if (ms < 1000) return `${ms} ms`
  return `${(ms / 1000).toFixed(1)} วินาที`
}

function scanTitle(scan: Scan): string {
  return `${thaiDateTime(scan.scanned_at)} · ${scan.passed} ผ่าน / ${scan.failed} ไม่ผ่าน / ${scan.advisory} ต้องตรวจด้วยมือ`
}

export function Multicloud() {
  const { me } = useAuth()
  const canScan = me?.role === 'owner' || me?.role === 'admin' || me?.role === 'audit_manager'
  const qc = useQueryClient()

  const [selectedId, setSelectedId] = useState('')
  const [status, setStatus] = useState<'all' | Status>('all')
  const [category, setCategory] = useState<'all' | Category>('all')
  const [notice, setNotice] = useState('')

  const controls = useQuery({
    queryKey: ['multicloud-controls'],
    queryFn: () => list<Control>('/v1/multicloud/controls'),
    staleTime: 60_000,
  })

  const scans = useQuery({
    queryKey: ['multicloud-scans'],
    queryFn: () => list<Scan>('/v1/multicloud/scans?limit=20'),
  })

  const activeId = selectedId || scans.data?.[0]?.id || ''
  const detail = useQuery({
    queryKey: ['multicloud-results', activeId],
    queryFn: () => api<ResultsResponse>(`/v1/multicloud/results?scan_id=${activeId}`),
    enabled: activeId !== '',
    retry: false,
  })

  const runScan = useMutation({
    mutationFn: () => apiWrite<ScanResponse>('/v1/multicloud/scan', 'POST', {}),
    onSuccess: (saved) => {
      setSelectedId(saved.scan.id)
      setNotice(
        `สแกนเสร็จใน ${duration(saved.scan.duration_ms)} · ${saved.scan.files_scanned} ไฟล์ · ` +
        `${saved.scan.passed} ผ่าน / ${saved.scan.failed} ไม่ผ่าน / ${saved.scan.advisory} ต้องตรวจด้วยมือ`,
      )
      void qc.invalidateQueries({ queryKey: ['multicloud-scans'] })
      qc.setQueryData(['multicloud-results', saved.scan.id], { scan: saved.scan, results: saved.results })
    },
    onError: (err) => setNotice(errorText(err, 'สแกนรีโปไม่สำเร็จ')),
  })

  const current = detail.data?.scan ?? null
  const results = useMemo(() => detail.data?.results ?? [], [detail.data])

  const filtered = useMemo(() => results.filter((row) => {
    if (status !== 'all' && row.status !== status) return false
    if (category !== 'all') {
      const control = controls.data?.find((c) => c.code === row.control_code)
      if (control?.category !== category) return false
    }
    return true
  }), [results, status, category, controls.data])

  const byCode = useMemo(() => {
    const map = new Map<string, Control>()
    for (const control of controls.data ?? []) map.set(control.code, control)
    return map
  }, [controls.data])

  const enforceable = (controls.data ?? []).filter((c) => c.enforceable).length
  const advisory = (controls.data ?? []).filter((c) => !c.enforceable).length

  return (
    <>
      <PageHead
        title="ความปลอดภัยหลายคลาวด์และนโยบาย"
        sub="สแกนไฟล์ตั้งค่าของรีโปนี้แบบคงที่: Cloudflare Worker, Docker Compose และ Kubernetes · ไม่เรียก API ของคลาวด์ใด ๆ · ผ่านจากไฟล์ไม่เท่ากับการบังคับใช้จริง"
        actions={canScan ? (
          <button
            className="btn btn-primary"
            onClick={() => { setNotice(''); runScan.mutate() }}
            disabled={runScan.isPending}
          >
            {runScan.isPending ? 'กำลังสแกน…' : 'สแกนรีโป'}
          </button>
        ) : undefined}
      />

      <div className="row" style={{ gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
        <span className="chip chip-on">รีโป {current?.repo_name ?? 'AuditAI'}</span>
        <span className="chip">ไฟล์ที่สแกน {current?.files_scanned ?? 0}</span>
        <span className="chip">ควบคุมที่ตัดสินจากไฟล์ได้ {enforceable}</span>
        <span className="chip">ควบคุมที่ต้องตรวจด้วยมือ {advisory}</span>
        <span className="chip">สแกนล่าสุด {current ? thaiDateTime(current.scanned_at) : 'ยังไม่มี'}</span>
      </div>

      {notice ? (
        <div className="card card-pad" role="status" style={{ marginBottom: 16 }}>
          <span className="dim" style={{ fontSize: 12.5 }}>{notice}</span>
        </div>
      ) : null}

      {scans.isLoading ? (
        <div className="stack"><Skel h={90} /><Skel h={240} /></div>
      ) : current === null ? (
        <div className="card card-pad">
          <Empty
            title="ยังไม่มีการสแกนนโยบาย"
            body="เริ่มสแกนครั้งแรก ระบบจะอ่าน wrangler.toml, docker-compose.ci.yml และ manifest ใน infra/k8s แล้วบันทึกผลรายข้อพร้อมไฟล์และบรรทัด"
          />
          {canScan ? (
            <div className="row" style={{ justifyContent: 'center', marginTop: 16 }}>
              <button className="btn btn-primary" onClick={() => runScan.mutate()} disabled={runScan.isPending}>
                {runScan.isPending ? 'กำลังสแกน…' : 'เริ่มสแกน'}
              </button>
            </div>
          ) : null}
        </div>
      ) : (
        <>
          <div className="grid-4" style={{ marginBottom: 16 }}>
            <Stat
              label="ผ่าน"
              value={current.passed}
              unit={`/ ${current.controls_total}`}
              foot={`ตัดสินจากไฟล์ ${current.files_scanned} ไฟล์`}
            />
            <Stat
              label="ไม่ผ่าน"
              value={<span style={{ color: current.failed > 0 ? 'var(--color-crimson)' : 'var(--color-emerald)' }}>{current.failed}</span>}
              foot={current.failed > 0 ? 'มีหลักฐานไฟล์และบรรทัดในตารางด้านล่าง' : 'ไม่พบข้อบกพร่องในไฟล์ที่ตรวจ'}
            />
            <Stat
              label="ต้องตรวจด้วยมือ"
              value={current.advisory}
              foot={current.advisory > 0 ? 'ควบคุมที่ตัดสินจากไฟล์ไม่ได้ เช่น RLS ของฐานข้อมูล' : 'ทุกควบคุมตัดสินได้จากไฟล์'}
            />
            <Stat
              label="ใช้เวลา"
              value={duration(current.duration_ms)}
              foot={`สั่งโดย ${current.triggered_by || 'ระบบ'} · ${thaiDateTime(current.scanned_at)}`}
            />
          </div>

          <div className="card card-pad" style={{ marginBottom: 16 }}>
            <div className="row" style={{ justifyContent: 'space-between', flexWrap: 'wrap', gap: 10 }}>
              <div>
                <div className="card-label" style={{ marginBottom: 6 }}>ขอบเขตของการสแกน</div>
                <div className="dim" style={{ fontSize: 12.5, maxWidth: 720, lineHeight: 1.65 }}>
                  ตรวจเฉพาะไฟล์ที่มีอยู่ในรีโป: apps/edge/wrangler.toml, docker-compose.ci.yml และ
                  infra/k8s/*.yaml ไม่มีการเรียก Cloudflare API, Docker daemon หรือ Kubernetes API
                  และไม่ใช้ credential ใด ๆ ผลจึงเป็นหลักฐานของไฟล์ ไม่ใช่การยืนยันว่า runtime
                  บังคับใช้จริง — NetworkPolicy จะผ่านก็ต่อเมื่อ CNI รองรับ และ annotation ของ
                  Ingress จะผ่านก็ต่อเมื่อ controller อ่านค่านั้น
                </div>
              </div>
              <div className="row" style={{ gap: 8 }}>
                <select
                  className="input"
                  style={{ width: 280, height: 32 }}
                  value={activeId}
                  onChange={(event) => setSelectedId(event.target.value)}
                  aria-label="เลือกผลสแกน"
                >
                  {(scans.data ?? []).map((scan) => (
                    <option key={scan.id} value={scan.id}>{scanTitle(scan)}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          <div className="card card-pad" style={{ marginBottom: 16 }}>
            <div className="row" style={{ justifyContent: 'space-between', flexWrap: 'wrap', gap: 10, marginBottom: 10 }}>
              <div className="card-label">ผลรายควบคุม</div>
              <div className="row" style={{ gap: 8 }}>
                <select
                  className="input"
                  style={{ width: 190, height: 32 }}
                  value={category}
                  onChange={(event) => setCategory(event.target.value as 'all' | Category)}
                  aria-label="กรองตามแพลตฟอร์ม"
                >
                  <option value="all">ทุกแพลตฟอร์ม</option>
                  {CATEGORY_ORDER.map((value) => (
                    <option key={value} value={value}>{CATEGORY_LABEL[value]}</option>
                  ))}
                </select>
                <select
                  className="input"
                  style={{ width: 170, height: 32 }}
                  value={status}
                  onChange={(event) => setStatus(event.target.value as 'all' | Status)}
                  aria-label="กรองตามสถานะ"
                >
                  <option value="all">ทุกสถานะ</option>
                  <option value="pass">ผ่าน</option>
                  <option value="fail">ไม่ผ่าน</option>
                  <option value="advisory">ต้องตรวจด้วยมือ</option>
                </select>
              </div>
            </div>

            {detail.isLoading ? (
              <Skel h={220} />
            ) : filtered.length === 0 ? (
              <Empty title="ไม่พบรายการที่ตรงกับตัวกรอง" body="เลือกสถานะหรือแพลตฟอร์มอื่นเพื่อดูผลที่เหลือ" />
            ) : (
              <div className="table-wrap" style={{ overflowX: 'auto' }}>
                <table className="table data">
                  <thead>
                    <tr>
                      <th>สถานะ</th>
                      <th>ควบคุม</th>
                      <th>แพลตฟอร์ม</th>
                      <th>ความรุนแรง</th>
                      <th>สรุปผลและหลักฐาน</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((row) => {
                      const control = byCode.get(row.control_code)
                      return (
                        <tr key={row.id}>
                          <td>
                            <span className="sev" style={{ color: STATUS_TONE[row.status] }}>
                              <i className="sev-dot" style={{ background: STATUS_TONE[row.status] }} />
                              {STATUS_LABEL[row.status]}
                            </span>
                          </td>
                          <td style={{ minWidth: 240 }}>
                            <div>{control?.title ?? row.control_code}</div>
                            <div className="dim mono" style={{ fontSize: 10.5, marginTop: 3 }}>
                              {row.control_code}
                              {control && !control.enforceable ? ' · ต้องตรวจกับระบบจริง' : ''}
                            </div>
                          </td>
                          <td className="dim" style={{ fontSize: 12 }}>
                            {control ? CATEGORY_LABEL[control.category] : '—'}
                          </td>
                          <td>
                            <span className={`sev sev-${row.severity}`}>
                              <i className="sev-dot" />
                              {SEVERITY_LABEL[row.severity]}
                            </span>
                          </td>
                          <td style={{ maxWidth: 680, whiteSpace: 'normal' }}>
                            <div style={{ fontSize: 12.5 }}>{row.summary}</div>
                            {row.evidence.length === 0 ? (
                              <div className="dim" style={{ fontSize: 11.5, marginTop: 6 }}>
                                ไม่มีหลักฐานไฟล์สำหรับข้อนี้
                              </div>
                            ) : (
                              <details style={{ marginTop: 8 }}>
                                <summary className="dim" style={{ fontSize: 11.5, cursor: 'pointer' }}>
                                  หลักฐาน {row.evidence.length} รายการ
                                </summary>
                                <div className="stack" style={{ marginTop: 8 }}>
                                  {row.evidence.map((ev, index) => (
                                    <div key={`${row.id}-${index}`} style={{ borderLeft: `2px solid ${STATUS_TONE[row.status]}`, paddingLeft: 10 }}>
                                      <div className="mono dim" style={{ fontSize: 10.5 }}>
                                        {ev.path}:{ev.line}
                                      </div>
                                      {ev.snippet ? (
                                        <code className="mono" style={{ fontSize: 11.5, display: 'block', marginTop: 3, whiteSpace: 'pre-wrap' }}>
                                          {ev.snippet}
                                        </code>
                                      ) : null}
                                      {ev.note ? (
                                        <div className="dim" style={{ fontSize: 11, marginTop: 3 }}>{ev.note}</div>
                                      ) : null}
                                    </div>
                                  ))}
                                </div>
                              </details>
                            )}
                            {control ? (
                              <div className="dim" style={{ fontSize: 10.5, marginTop: 6 }}>{control.reference}</div>
                            ) : null}
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <div className="card card-pad">
            <div className="card-label" style={{ marginBottom: 10 }}>ทะเบียนควบคุม</div>
            <div className="table-wrap" style={{ overflowX: 'auto' }}>
              <table className="table data">
                <thead>
                  <tr>
                    <th>รหัส</th>
                    <th>ชื่อควบคุม</th>
                    <th>แพลตฟอร์ม</th>
                    <th>ความรุนแรง</th>
                    <th>ตัดสินจากไฟล์ได้</th>
                    <th>เหตุผล</th>
                  </tr>
                </thead>
                <tbody>
                  {(controls.data ?? []).map((control) => (
                    <tr key={control.id}>
                      <td className="mono" style={{ fontSize: 11.5 }}>{control.code}</td>
                      <td style={{ minWidth: 250 }}>
                        <div>{control.title}</div>
                        <div className="dim" style={{ fontSize: 11, marginTop: 3 }}>{control.description}</div>
                      </td>
                      <td className="dim" style={{ fontSize: 12 }}>{CATEGORY_LABEL[control.category]}</td>
                      <td>
                        <span className={`sev sev-${control.severity}`}>
                          <i className="sev-dot" />
                          {SEVERITY_LABEL[control.severity]}
                        </span>
                      </td>
                      <td>
                        <span className="chip" style={{ color: control.enforceable ? 'var(--color-emerald-soft)' : 'var(--color-amber)' }}>
                          {control.enforceable ? 'ได้' : 'ไม่ได้'}
                        </span>
                      </td>
                      <td className="dim" style={{ fontSize: 11.5, maxWidth: 420, whiteSpace: 'normal' }}>
                        {control.rationale || '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </>
  )
}
